import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClock,
  faEye,
  faFileAlt,
  faSpinner,
  faStamp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  return fetch(path, {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
}

const STATUS_TABS = ["pending", "approved", "rejected", "all"];

function StatusBadge({ status }) {
  return <span className={`adm-badge ${status || ""}`}>{status || "-"}</span>;
}

export default function AdminPropertyVerificationQueue() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [badgeVisible, setBadgeVisible] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const totalPages = Math.max(1, Math.ceil((total || 0) / 20));

  const loadRows = useCallback(async (nextPage = page, nextStatus = status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        limit: "20",
        status: nextStatus,
      });
      const res = await adminFetch(`/api/admin/property-verifications?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Failed to load verification queue." });
        return;
      }
      setRows(Array.isArray(data.submissions) ? data.submissions : []);
      setTotal(Number(data.total || 0));
    } catch {
      setMsg({ type: "error", text: "Unable to reach server." });
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    loadRows(page, status);
  }, [loadRows, page, status]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    setSelected(null);
    setReviewerNotes("");
    setRejectionReason("");
    setMsg({ type: "", text: "" });
    try {
      const res = await adminFetch(`/api/admin/property-verifications/${id}`);
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Failed to load verification details." });
        return;
      }
      setSelected(data);
      setReviewerNotes(data.reviewerNotes || "");
      setBadgeVisible(Boolean(data.badgeVisible));
    } catch {
      setMsg({ type: "error", text: "Unable to load verification details." });
    } finally {
      setLoadingDetail(false);
    }
  };

  const openDocument = async (requestId, field) => {
    const token = localStorage.getItem("adminToken");
    try {
      const res = await fetch(`/api/admin/property-verification-document/${requestId}/${field}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      setMsg({ type: "error", text: "Unable to open document." });
    }
  };

  const approve = async () => {
    if (!selected) return;
    setProcessing(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await adminFetch(`/api/admin/property-verifications/${selected.id}/approve`, {
        method: "PATCH",
        body: JSON.stringify({ reviewerNotes, badgeVisible }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Failed to approve verification." });
        return;
      }
      setMsg({ type: "success", text: "Verification approved." });
      await openDetail(selected.id);
      await loadRows(page, status);
    } catch {
      setMsg({ type: "error", text: "Unable to approve verification." });
    } finally {
      setProcessing(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    if (!rejectionReason.trim()) {
      setMsg({ type: "error", text: "Rejection reason is required." });
      return;
    }

    setProcessing(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await adminFetch(`/api/admin/property-verifications/${selected.id}/reject`, {
        method: "PATCH",
        body: JSON.stringify({ reviewerNotes, rejectionReason }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Failed to reject verification." });
        return;
      }
      setMsg({ type: "success", text: "Verification rejected." });
      await openDetail(selected.id);
      await loadRows(page, status);
    } catch {
      setMsg({ type: "error", text: "Unable to reject verification." });
    } finally {
      setProcessing(false);
    }
  };

  const updateBadgeVisibility = async (nextValue) => {
    if (!selected) return;
    setProcessing(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await adminFetch(`/api/admin/properties/${selected.propertyId}/verification-visibility`, {
        method: "PATCH",
        body: JSON.stringify({ badgeVisible: nextValue }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Failed to update badge visibility." });
        return;
      }
      setBadgeVisible(nextValue);
      setMsg({ type: "success", text: "Badge visibility updated." });
      await openDetail(selected.id);
      await loadRows(page, status);
    } catch {
      setMsg({ type: "error", text: "Unable to update badge visibility." });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout pageTitle="Property Verification Queue">
      {msg.text && <div className={`adm-alert ${msg.type}`}>{msg.text}</div>}

      <div className="adm-filter-tabs" style={{ marginBottom: "1rem" }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            className={`adm-filter-tab ${status === tab ? "active" : ""}`}
            onClick={() => {
              setStatus(tab);
              setPage(1);
            }}
          >
            {tab === "all" ? "All" : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Verification Requests</h3>
          <span style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>{total} total</span>
        </div>

        {loading ? (
          <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>
        ) : rows.length === 0 ? (
          <div className="adm-empty">
            <FontAwesomeIcon icon={faStamp} />
            <p>No property verification requests in this filter.</p>
          </div>
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Reviewed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{row.property_name || "Unknown"}</div>
                      <div style={{ fontSize: ".75rem", color: "var(--adm-muted)" }}>{row.property_type || "Property"}</div>
                    </td>
                    <td>
                      <div>{[row.owner_firstname, row.owner_lastname].filter(Boolean).join(" ") || "Unknown"}</div>
                      <div style={{ fontSize: ".75rem", color: "var(--adm-muted)" }}>{row.owner_email || "-"}</div>
                    </td>
                    <td><StatusBadge status={row.status} /></td>
                    <td>{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "-"}</td>
                    <td>{row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : "-"}</td>
                    <td>
                      <button className="adm-btn sm ghost" onClick={() => openDetail(row.id)}>
                        <FontAwesomeIcon icon={faEye} />
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="adm-pagination" style={{ marginTop: ".8rem" }}>
        <div className="adm-pag-meta">Page {page} of {totalPages}</div>
        <div className="adm-pag-btns">
          <button className="adm-pag-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <button className="adm-pag-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      {(selected || loadingDetail) && (
        <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="adm-panel" style={{ width: "min(1000px, 94vw)" }}>
            <div className="adm-panel-header">
              <h4>{loadingDetail ? "Loading..." : `Property Verification #${selected?.id}`}</h4>
              <button className="adm-panel-close" onClick={() => setSelected(null)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="adm-panel-body">
              {loadingDetail && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}

              {!loadingDetail && selected && (
                <>
                  <div className="adm-card" style={{ marginBottom: "1rem" }}>
                    <div className="adm-card-body">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: ".75rem" }}>
                        <div><strong>Property</strong><div>{selected.property?.name || "-"}</div></div>
                        <div><strong>Owner</strong><div>{selected.owner?.name || "-"}</div></div>
                        <div><strong>Status</strong><div><StatusBadge status={selected.status} /></div></div>
                        <div><strong>Submitted</strong><div>{selected.submittedAt ? new Date(selected.submittedAt).toLocaleString() : "-"}</div></div>
                      </div>

                      {selected.supportingNotes && (
                        <div style={{ marginTop: ".8rem" }}>
                          <strong>Owner Notes</strong>
                          <div style={{ marginTop: ".3rem", fontSize: ".86rem", color: "var(--adm-muted)" }}>{selected.supportingNotes}</div>
                        </div>
                      )}

                      {selected.rejectionReason && (
                        <div className="adm-alert error" style={{ marginTop: ".8rem" }}>
                          Rejection reason: {selected.rejectionReason}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="adm-card" style={{ marginBottom: "1rem" }}>
                    <div className="adm-card-header"><h3>Documents</h3></div>
                    <div className="adm-card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: ".65rem" }}>
                      {(selected.documents || []).map((doc) => (
                        <div key={doc.field} style={{ border: "1px solid var(--adm-border)", borderRadius: 10, padding: ".7rem" }}>
                          <div style={{ fontWeight: 700, marginBottom: ".25rem" }}>{doc.label}</div>
                          <div style={{ fontSize: ".76rem", color: "var(--adm-muted)", marginBottom: ".45rem" }}>
                            {doc.uploaded ? doc.filename : "Not uploaded"}
                          </div>
                          <button
                            className="adm-btn sm outline"
                            disabled={!doc.uploaded}
                            onClick={() => openDocument(selected.id, doc.field)}
                          >
                            <FontAwesomeIcon icon={faFileAlt} />
                            Open
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="adm-card">
                    <div className="adm-card-header"><h3>Review Decision</h3></div>
                    <div className="adm-card-body">
                      <div className="adm-field">
                        <label>Reviewer Notes</label>
                        <textarea
                          rows={3}
                          value={reviewerNotes}
                          onChange={(e) => setReviewerNotes(e.target.value)}
                          placeholder="Internal review notes (optional)"
                        />
                      </div>

                      {(selected.status === "pending" || selected.status === "approved") && (
                        <div className="adm-field" style={{ marginTop: ".45rem" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: ".55rem" }}>
                            <input
                              type="checkbox"
                              checked={badgeVisible}
                              onChange={(e) => setBadgeVisible(e.target.checked)}
                            />
                            Show verified badge publicly when approved
                          </label>
                        </div>
                      )}

                      {selected.status === "pending" && (
                        <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: ".75rem" }}>
                          <button className="adm-btn success" onClick={approve} disabled={processing}>
                            {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                            Approve
                          </button>
                        </div>
                      )}

                      {(selected.status === "pending" || selected.status === "approved") && (
                        <div style={{ marginTop: ".75rem" }}>
                          <div className="adm-field">
                            <label>Rejection Reason</label>
                            <textarea
                              rows={2}
                              value={rejectionReason}
                              onChange={(e) => setRejectionReason(e.target.value)}
                              placeholder="Required when rejecting"
                            />
                          </div>
                          <div style={{ display: "flex", gap: ".6rem", marginTop: ".55rem", flexWrap: "wrap" }}>
                            {selected.status === "pending" && (
                              <button className="adm-btn danger" onClick={reject} disabled={processing}>
                                {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />}
                                Reject
                              </button>
                            )}
                            {selected.status === "approved" && (
                              <button className="adm-btn ghost" onClick={() => updateBadgeVisibility(!badgeVisible)} disabled={processing}>
                                {processing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faClock} />}
                                {badgeVisible ? "Hide Badge" : "Show Badge"}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
